import { USER_ROLES, type UserRoleType } from '@/constants/roles';

// Role values can come from JWT payloads, query params, or undefined initial state.
type RoleValue = string | undefined | null;

// Normalizes untrusted role input to a known app role. Unknown roles are treated as null.
export const normalizeUserRole = (role: RoleValue): UserRoleType | null => {
  if (!role) return null;
  return (Object.values(USER_ROLES) as string[]).includes(role) ? (role as UserRoleType) : null;
};

// Strict system-admin check.
export const isAdminRole = (role: RoleValue): boolean =>
  normalizeUserRole(role) === USER_ROLES.ADMIN;

// Admin-equivalent roles share most platform capabilities.
export const isAdminEquivalentRole = (role: RoleValue): boolean => {
  const normalized = normalizeUserRole(role);
  return normalized === USER_ROLES.ADMIN || normalized === USER_ROLES.DEVELOPER;
};

// Can open /admin area and admin routes.
export const canAccessAdminArea = (role: RoleValue): boolean => isAdminEquivalentRole(role);

// Can use admin client/company selector context.
export const canUseAdminTenantScope = (role: RoleValue): boolean => isAdminEquivalentRole(role);

// Can bypass explicit route role constraints for module routes.
export const canBypassModuleRoleRequirements = (role: RoleValue): boolean => isAdminEquivalentRole(role);

// Intentionally stricter than admin-equivalent: only true ADMIN can change global user roles.
export const canEditGlobalUserRole = (role: RoleValue): boolean => isAdminRole(role);

